/**
 * The shape of an `account_deletions/{uid}` document.
 *
 * DUPLICATED, deliberately. The source of truth is
 * obsessless-cloud-functions/src/users/deletionAudit.ts (the record) and
 * .../eraseUserData.ts (the per-location result). db-admin cannot import from that
 * package, and it could not use the types unchanged anyway: the functions declare
 * `admin.firestore.Timestamp`, while a browser read returns the client-SDK
 * `Timestamp`. Change those files first, then mirror the change here.
 *
 * The record holds enum values, counts, timestamps and error CODES only -- no email,
 * no display name, no IP, no free text. The uid is the only identifier on screen, and
 * after a completed sweep no store maps it to a person.
 */
import type { Timestamp } from 'firebase/firestore';

export type DeletionStatus = 'in_progress' | 'completed' | 'failed';

/** `user` = the Auth trigger fired. `backfill` = a tools/ script swept an already-deleted uid. */
export type DeletionSource = 'user' | 'backfill';

export type RevenueCatOutcome = 'anonymized' | 'not-found' | 'failed';

export interface RevenueCatAudit {
  outcome: RevenueCatOutcome;
  attributesCleared: number;
  /** Vendor-call retries, separate from the record's own attemptCount. */
  attemptCount: number;
  errorCode?: string;
}

export type PostHogOutcome = 'queued' | 'not-found' | 'failed';

export interface PostHogAudit {
  outcome: PostHogOutcome;
  /** Vendor-call retries, separate from the record's own attemptCount. */
  attemptCount: number;
  errorCode?: string;
}

export interface LocationResult {
  status: 'deleted' | 'failed';
  documentsDeleted: number;
  errorCode?: string;
}

export interface AccountDeletionRecord {
  /** The document id. Also the RevenueCat app_user_id, which the API cannot rename. */
  uid: string;
  status: DeletionStatus;
  source: DeletionSource;
  requestedAt: Timestamp;
  updatedAt: Timestamp;
  completedAt: Timestamp | null;
  attemptCount: number;
  /** Which user-data-map.json version produced this run. */
  registryVersion: number;
  /** null until step 1 reports. RevenueCat runs BEFORE the Firestore sweep. */
  revenueCat: RevenueCatAudit | null;
  /**
   * null until step 2 reports. Also null on records completed before the PostHog step
   * existed, so null on a completed record is not a fault.
   */
  posthog: PostHogAudit | null;
  /** Location id -> what happened to it. Empty until step 3 runs. Keys are dynamic. */
  firestore: Record<string, LocationResult>;
}

export interface DeletionHealthCounts {
  inProgress: number;
  completed: number;
  failed: number;
  /** status in (in_progress, failed) and untouched for longer than the UI threshold. */
  stalled: number;
}

export const STATUS_LABELS: Record<DeletionStatus, string> = {
  in_progress: 'In progress',
  completed: 'Completed',
  failed: 'Failed',
};

export const REVENUECAT_OUTCOME_LABELS: Record<RevenueCatOutcome, string> = {
  anonymized: 'Anonymized',
  'not-found': 'No customer',
  failed: 'Failed',
};

export const POSTHOG_OUTCOME_LABELS: Record<PostHogOutcome, string> = {
  queued: 'Queued',
  'not-found': 'No person',
  failed: 'Failed',
};
